import { Trans, useLingui } from '@lingui/react/macro';

import { UserAvatar } from '@/components/UserAvatar/UserAvatar';
import { UserAvatarGroup } from '@/components/UserAvatarGroup/UserAvatarGroup';
import { FilterCheckboxItem } from '@/design_system/Filters/FiltersMenu/FilterCheckboxItem';
import { FilterSearch } from '@/design_system/Filters/FiltersMenu/FilterSearch';
import {
  FilterItemNoResults,
  FiltersMenu,
  FiltersSubMenu,
} from '@/design_system/Filters/FiltersMenu/FiltersMenu';
import { FilterTag } from '@/design_system/Filters/FiltersMenu/FilterTag';
import IconUsers from '@/icons/Users.svg';
import { useInfiniteUsers } from '@/models/user';
import useDebouncedState from '@/utils/useDebouncedState';

import { useFilters } from '../FiltersState';

const MINIMUM_USERS_FOR_SEARCH = 5;
const USERS_PER_PAGE = 20;

export const FiltersCollaborator = ({ tag }: { tag?: boolean }) => {
  const { t } = useLingui();

  const {
    collaborators: { collaborators, setCollaborators },
  } = useFilters();

  const [search, debouncedSearch, setSearch] = useDebouncedState<string>('', 300);

  // The first hook is to get the total count of users
  // React-query will only fetch once, even with two hooks (as long as the params are the same)
  const { data: initialData } = useInfiniteUsers({
    limit: USERS_PER_PAGE,
  });
  const { data, fetchNextPage, isLoading } = useInfiniteUsers({
    limit: USERS_PER_PAGE,
    search: debouncedSearch || undefined,
  });

  const users = data?.pages.flatMap((page) => page.users) ?? [];
  const count = initialData?.pages[0]?.meta.count || 0;

  const collaboratorsIds = collaborators.map(({ id }) => id);

  const options =
    users.length > 0 ? (
      users.map((user) => (
        <FilterCheckboxItem
          key={user.id}
          checked={collaboratorsIds.includes(user.id)}
          onChange={(checked) => {
            if (checked) {
              setCollaborators([...collaborators, { id: user.id, name: user.name }]);
            } else {
              setCollaborators(collaborators.filter((collaborator) => collaborator.id !== user.id));
            }
          }}
        >
          <UserAvatar user={user} size="x-small" />
          <span className="text-ellipsis">{user.name}</span>
        </FilterCheckboxItem>
      ))
    ) : (
      <FilterItemNoResults />
    );

  const searchWithOptions = (
    <FilterSearch
      inputProps={{
        ariaLabel: t({
          id: 'requests.filters.collaborator.search.label',
          message: 'Search collaborators',
        }),
        placeholder: t({
          id: 'requests.filters.collaborator.search.label',
          message: 'Search collaborators',
        }),
        value: search,
        onChange: setSearch,
      }}
      onLoadMore={fetchNextPage}
      isLoading={users.length === 0 && isLoading}
      isLoadingMore={isLoading}
    >
      {options}
    </FilterSearch>
  );

  const selectedCollaboratorsCount = collaborators.length;
  const children = count < MINIMUM_USERS_FOR_SEARCH ? options : searchWithOptions;

  if (tag) {
    if (collaborators.length === 0) {
      return null;
    }

    return (
      <FiltersMenu
        button={
          <FilterTag
            label={t({ id: 'requests.filters.collaborator.label', message: 'Collaborator' })}
            value={
              selectedCollaboratorsCount === 1 ? (
                <>
                  <UserAvatar user={collaborators[0]} size="x-small" />
                  {collaborators[0].name}
                </>
              ) : (
                <>
                  <UserAvatarGroup users={collaborators} size="x-small" withTooltip={false} />
                  <Trans id="requests.filters.collaborator.multiple">
                    {selectedCollaboratorsCount} collaborators
                  </Trans>
                </>
              )
            }
            tooltip={
              selectedCollaboratorsCount > 1
                ? collaborators.map((user) => user.name).join(', ')
                : undefined
            }
            onClear={() => setCollaborators([])}
          />
        }
      >
        {children}
      </FiltersMenu>
    );
  }

  return (
    <FiltersSubMenu
      icon={<IconUsers />}
      label={t({ id: 'requests.filters.collaborator.label', message: 'Collaborator' })}
    >
      {children}
    </FiltersSubMenu>
  );
};
